import { useEffect, useMemo } from 'react';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { usePreferredPaymentMethod } from '@sorare/core/src/hooks/wallets/usePreferredPaymentMethod';
import {
  OrderedPaymentMethod,
  PaymentMethod,
} from '@sorare/core/src/lib/paymentMethod';

import { usePaymentContext } from 'components/buyActions/Context';

type Props = {
  selectedPaymentMethod: OrderedPaymentMethod | undefined;
  setSelectedPaymentMethod: (p: OrderedPaymentMethod) => void;
  currencyAvailableOptionsValues: OrderedPaymentMethod[];
  onlyCreditCards?: boolean;
};

export const useOrderedPaymentMethods = ({
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  onlyCreditCards,
  currencyAvailableOptionsValues,
}: Props) => {
  const {
    preferredPaymentMethodIsCreditCard,
    preferredOrderedPaymentMethodGrouped,
  } = usePreferredPaymentMethod();

  const {
    walletPreferences: { showEthWallet },
  } = useCurrentUserContext();

  const {
    insufficientFundsInFiatWallet,
    insufficientFundsInEthWallet,
    paymentMethodsLoading,
    isFiat,
    currencies,
  } = usePaymentContext();
  const fiatPaymentEnabled = currencies.includes(Currency.FIAT);
  const ethPaymentEnabled = currencies.includes(Currency.ETH);

  const defaultOrderedPaymentMethods = useMemo(() => {
    const wallets = [
      ...(fiatPaymentEnabled && !onlyCreditCards
        ? [PaymentMethod.FIAT_WALLET]
        : []),
      ...(showEthWallet && ethPaymentEnabled && !onlyCreditCards
        ? [PaymentMethod.ETH_WALLET]
        : []),
    ];
    if (!isFiat) wallets.reverse();
    return [
      ...wallets,
      PaymentMethod.CREDIT_CARD,
      PaymentMethod.PAYMENT_REQUEST,
      PaymentMethod.PAYPAL,
    ].filter(o => currencyAvailableOptionsValues.includes(o));
  }, [
    fiatPaymentEnabled,
    showEthWallet,
    ethPaymentEnabled,
    isFiat,
    onlyCreditCards,
    currencyAvailableOptionsValues,
  ]);

  useEffect(() => {
    if (
      (!selectedPaymentMethod && !paymentMethodsLoading) ||
      (selectedPaymentMethod &&
        !currencyAvailableOptionsValues.includes(selectedPaymentMethod))
    ) {
      if (
        preferredOrderedPaymentMethodGrouped &&
        currencyAvailableOptionsValues.includes(
          preferredOrderedPaymentMethodGrouped
        ) &&
        (!preferredPaymentMethodIsCreditCard ||
          preferredOrderedPaymentMethodGrouped) &&
        !(
          insufficientFundsInFiatWallet &&
          preferredOrderedPaymentMethodGrouped === PaymentMethod.FIAT_WALLET
        ) &&
        !(
          insufficientFundsInEthWallet &&
          preferredOrderedPaymentMethodGrouped === PaymentMethod.ETH_WALLET
        )
      ) {
        const withPreferredPaymentMethod = [
          preferredOrderedPaymentMethodGrouped,
          ...currencyAvailableOptionsValues.filter(
            pm => pm !== preferredOrderedPaymentMethodGrouped
          ),
        ];
        setSelectedPaymentMethod(withPreferredPaymentMethod[0]);
      } else if (isFiat && insufficientFundsInFiatWallet) {
        setSelectedPaymentMethod(PaymentMethod.CREDIT_CARD);
      } else {
        setSelectedPaymentMethod(defaultOrderedPaymentMethods[0]);
      }
    }
  }, [
    defaultOrderedPaymentMethods,
    insufficientFundsInFiatWallet,
    isFiat,
    paymentMethodsLoading,
    setSelectedPaymentMethod,
    preferredOrderedPaymentMethodGrouped,
    preferredPaymentMethodIsCreditCard,
    insufficientFundsInEthWallet,
    currencyAvailableOptionsValues,
    selectedPaymentMethod,
  ]);
};
